import { each, includes, reduce, uniq } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { Clause, InnerClause } from "@radarly/models/clause";
import {
  getFieldFromInnerClause,
  getInnerClauseFromClause,
} from "@radarly/models/clause/getter";
import {
  isCustomFieldsClause,
  isNotClause,
  isSandboxedClause,
  isTermClause,
} from "@radarly/models/clause/is";
import { hardMergeClauses } from "@radarly/models/clause/merge";
import {
  refineClauseWithInnerClause,
  refineClauseWithoutField,
} from "@radarly/models/clause/refiner";
import { transformToCustomFieldClause } from "@radarly/models/clause/transformer";
import { FullFacet } from "@radarly/models/facets";
import { Status } from "@radarly/models/filters";

export function isExcludedFromFacet(
  clause: Clause,
  facet: FullFacet,
  value: string | number
) {
  return reduce(
    clause.and,
    (acc: boolean, innerClause) => {
      if (
        isNotClause(innerClause) &&
        getFieldFromInnerClause(innerClause) === facet.id
      ) {
        if (isCustomFieldsClause(innerClause.not) && facet.custom) {
          return includes(innerClause.not.customFields[facet.id], value);
        } else if (isTermClause(innerClause.not)) {
          return includes(innerClause.not[facet.id], value);
        }
      }
      return acc;
    },
    false
  );
}

export function isIncludedFromFacet(
  clause: Clause,
  facet: FullFacet,
  value: string | number
) {
  return reduce(
    clause.and,
    (acc: boolean, innerClause) => {
      if (
        !isNotClause(innerClause) &&
        getFieldFromInnerClause(innerClause) === facet.id
      ) {
        if (isCustomFieldsClause(innerClause) && facet.custom) {
          return includes(innerClause.customFields[facet.id], value);
        } else if (isTermClause(innerClause)) {
          return includes(innerClause[facet.id], value);
        }
      }
      return acc;
    },
    false
  );
}

function getTermValuesFromClause(clause: Clause): Array<string | number> {
  function getTermValuesFromInnerClause(
    innerClause: InnerClause
  ): Array<string | number> {
    if (isTermClause(innerClause)) {
      return innerClause[getFieldFromInnerClause(innerClause)] || [];
    } else if (isCustomFieldsClause(innerClause)) {
      return getTermValuesFromInnerClause(innerClause.customFields) || [];
    } else if (isNotClause(innerClause)) {
      return getTermValuesFromInnerClause(innerClause.not) || [];
    } else {
      return [];
    }
  }

  if (clause && clause.and) {
    return reduce(
      clause.and,
      (acc, innerClause) => {
        return [...acc, ...getTermValuesFromInnerClause(innerClause)];
      },
      []
    );
  } else {
    return [];
  }
}

export function getNextClauseFromStatusAndValues(
  clause: Clause,
  facet: FullFacet,
  values: Array<string | number>,
  status: Status
) {
  const includedValues = [];
  const excludedValues = [];
  each(uniq([...getTermValuesFromClause(clause), ...values]), (_value) => {
    if (!includes(values, _value)) {
      if (isIncludedFromFacet(clause, facet, _value)) {
        includedValues.push(_value);
      } else if (isExcludedFromFacet(clause, facet, _value)) {
        excludedValues.push(_value);
      }
    } else {
      if (status === "include") {
        includedValues.push(_value);
      } else if (status === "exclude") {
        excludedValues.push(_value);
      }
    }
  });
  const andClause = {
    [facet.id]: includedValues,
  };
  const notClause = {
    not: {
      [facet.id]: excludedValues,
    },
  };
  return {
    and: [
      facet.custom ? transformToCustomFieldClause(andClause) : andClause,
      facet.custom ? transformToCustomFieldClause(notClause) : notClause,
    ],
  };
}

export function getNextClauseFromStatusAndClassifiedValues(
  clause: Clause,
  facet: FullFacet,
  impactedValues: { id: string | number; status: Status }[],
  preservedValues: { id: string | number; status: Status }[],
  status: Status
) {
  const includedValues = [];
  const excludedValues = [];
  const impactedValuesIds = impactedValues.map(({ id }) => id);
  const preservedValuesIds = preservedValues.map(({ id }) => id);
  const allValues = [...impactedValuesIds, ...preservedValuesIds];

  each(
    uniq([
      ...getTermValuesFromClause(clause),
      ...impactedValuesIds,
      ...preservedValuesIds,
    ]),
    (_value) => {
      if (!includes(allValues, _value)) {
        if (isIncludedFromFacet(clause, facet, _value)) {
          includedValues.push(_value);
        } else if (isExcludedFromFacet(clause, facet, _value)) {
          excludedValues.push(_value);
        }
      } else {
        const isNotPreservedValue = !preservedValuesIds.includes(_value);
        if (
          impactedValuesIds.includes(_value) &&
          isNotPreservedValue &&
          status === "include"
        ) {
          includedValues.push(_value);
        } else if (
          impactedValuesIds.includes(_value) &&
          isNotPreservedValue &&
          status === "exclude"
        ) {
          excludedValues.push(_value);
        } else {
          const valueStatus = preservedValues.find(
            (preservedValue) => preservedValue.id === _value
          )?.status;
          if (valueStatus === "include") {
            includedValues.push(_value);
          } else if (valueStatus === "exclude") {
            excludedValues.push(_value);
          }
        }
      }
    }
  );
  const andClause = {
    [facet.id]: includedValues,
  };
  const notClause = {
    not: {
      [facet.id]: excludedValues,
    },
  };
  return {
    and: [
      facet.custom ? transformToCustomFieldClause(andClause) : andClause,
      facet.custom ? transformToCustomFieldClause(notClause) : notClause,
    ],
  };
}

export function getNextClauseFromStatusAndValue(
  clause: Clause,
  facet: FullFacet,
  value: string | number,
  status: Status
) {
  return getNextClauseFromStatusAndValues(clause, facet, [value], status);
}

export function getNextClauseFromFlagValue(facet: FullFacet, flag: boolean) {
  return {
    and: [
      {
        [facet.id]: flag,
      },
    ],
  };
}

export function getNextClauseFromStringValue(facet: FullFacet, value: string) {
  return {
    and: [
      {
        [facet.id]: value,
      },
    ],
  };
}

export function getNextClauseFromSandboxedValue(
  clause: Clause,
  field: string,
  status: Status
): Clause {
  let prevClause = clause;
  if (field === "inRelationship" || field === "hasChildren") {
    const innerClause = getInnerClauseFromClause(clause, Facet.MaritalStatus);
    if (innerClause) {
      prevClause = refineClauseWithoutField(prevClause, Facet.MaritalStatus);
      innerClause[Facet.MaritalStatus].forEach(
        (value: string) =>
          (prevClause = refineClauseWithInnerClause(prevClause, {
            [value]: true,
          }))
      );
    }
  }
  return hardMergeClauses(
    { and: [{ [field]: status === "include" }] },
    prevClause
  );
}

export function deleteSandboxedClause(clause: Clause): Clause {
  let prevClause = clause;
  clause.and.forEach((innerClause) => {
    if (isSandboxedClause(innerClause)) {
      const field = getFieldFromInnerClause(innerClause);
      prevClause = refineClauseWithoutField(prevClause, field);
    }
  });
  return prevClause;
}
